import { useTranslation } from "react-i18next";
import { Avatar, Button, Input, Modal, Table, TableColumnsType } from 'antd';
import React, { useEffect, useState } from 'react';
import { getAvailableUniversities } from '../../../../../service/category-management/university-management';
import './MemberUniversityModal.scss';
import useDebounce from '../../../../../hooks/useDebounce';
const { Search } = Input;

type Props = {
  open: boolean;
  handleCancel: () => void;
  setMemberUniversity: (universities: University[]) => void;
  memberUniversity: any;
};

type University = {
  id: number;
  name: string;
  code: string;
  englishName?: string;
  logo?: string;
  uuid: string
};

const MemberUniversityModal = (props: Props) => {
  const { t } = useTranslation();
  const { handleCancel, open, setMemberUniversity, memberUniversity } = props;
  const [availableUniversities, setAvailableUniversities] = useState<University[]>([]);
  const [pageNumber, setPageNumber] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(10);
  const [loading, setLoading] = useState<boolean>(false);
  const [totalRecords, setTotalRecords] = useState<number>(0);
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [selectedRows, setSelectedRows] = useState<University[]>([]);
  const [searchKeyword, setSearchKeyword] = useState<string>('');
  const debouncedSearchKeyword = useDebounce(searchKeyword, 500);

  const columns: TableColumnsType<University> = [
    {
      title: t('STT', 'STT'),
      dataIndex: 'numericalOrder',
      key: 'numericalOrder',
      width: 50,
      render: (_: any, __: University, index: number) => {
        return <div>{index + 1 + (pageNumber - 1) * pageSize}</div>;
      },
    },
    {
      title: t('Mã trường', 'Mã trường'),
      dataIndex: 'code',
      key: 'code',
      width: 120,
    },
    {
      title: t('Tên trường', 'Tên trường'),
      dataIndex: 'name',
      key: 'name',
      width: 300,
      sorter: (a, b) => a.name.localeCompare(b.name),
      render: (text: string, record: University) => (
        <div className="flex align-center gap-8">
          <Avatar shape="square" size="small" src={record?.logo} />
          {record?.name}
        </div>
      ),
    },
  ];

  const handleTableChange = (pagination: any) => {
    setPageNumber(pagination.current);
    setPageSize(pagination.pageSize);
  };

  useEffect(() => {
    if (!open) return;

    const fetchAvailableUniversities = async () => {
      setLoading(true);
      const payload = {
        page: pageNumber,
        size: pageSize,
        keyword: debouncedSearchKeyword,
      };
      try {
        const res = await getAvailableUniversities(payload);
        setAvailableUniversities(
          res?.data?.data?.content.map((it: any) => ({
            ...it,
            enterpriseId: it.uuid,
          })),
        );
        setTotalRecords(res?.data?.data?.totalElements);
      } catch (error) {
        console.log(error);
      } finally {
        setLoading(false);
      }
    };

    fetchAvailableUniversities();
  }, [open, pageNumber, pageSize, debouncedSearchKeyword]);


  useEffect(() => {
    if (!open) return;
    setSearchKeyword("")
    setPageNumber(1)
  }, [open, memberUniversity]);


  useEffect(() => {
    if (memberUniversity && memberUniversity.length > 0) {
      const oldKeys = memberUniversity.map((it: any) => it.uuid || it.enterpriseId);
      setSelectedRowKeys(oldKeys);

      const selected = availableUniversities.filter(u =>
        oldKeys.includes(u.uuid)
      );
      setSelectedRows(selected);
    } else {
      setSelectedRowKeys([]);
      setSelectedRows([]);
    }
  }, [open, memberUniversity, availableUniversities]);

  let locale = {
    emptyText: (
      <span>
        <p>
          <img
            className="image-empty-data"
            src="/images/empty-img-gray.png"
            alt="empty-img"
          ></img>
        </p>
        <p className="nodata-text">{t('Không có dữ liệu', 'Không có dữ liệu')}</p>
      </span>
    ),
  };
  return (
    <Modal
      title={t("Trường đại học thành viên", "Trường đại học thành viên")}
      centered
      width={1200}
      onCancel={handleCancel}
      open={open}
      footer={
        <div className="flex gap-8">
          <Button onClick={handleCancel}>{t('Huỷ bỏ', 'Huỷ bỏ')}</Button>
          <Button
            type="primary"
            onClick={() => {
              const cleanedNewRows = selectedRows.filter(Boolean);
              const mergedResult = [...cleanedNewRows, ...memberUniversity];
              setMemberUniversity(mergedResult);
              handleCancel();
            }}
          >
            {t("Xác nhận", "Xác nhận")}
          </Button>

        </div>
      }
      className="member-university-modal"
    >
      <div style={{ marginBottom: 16 }}>
        <Search
          placeholder={t("Nhập từ khoá...", "Nhập từ khoá...")}
          allowClear
          enterButton={t("Tìm kiếm", "Tìm kiếm")}
          value={searchKeyword}
          onChange={(e) => {
            setSearchKeyword(e.target.value);
            setPageNumber(1);
          }}
          onSearch={() => {
            setPageNumber(1);
          }}
        />
      </div>
      <Table
        rowSelection={{
          preserveSelectedRowKeys: true,
          selectedRowKeys,
          onChange: (newSelectedRowKeys, _) => {
            const selected = availableUniversities.filter(u => newSelectedRowKeys.includes(u.uuid));
            setSelectedRowKeys(newSelectedRowKeys);
            setSelectedRows(selected);
          }
        }} columns={columns}
        dataSource={availableUniversities}
        loading={loading}
        pagination={{
          current: pageNumber,
          pageSize: pageSize,
          total: totalRecords,
          showSizeChanger: false,
        }}
        rowKey="uuid"
        onChange={handleTableChange}
        scroll={{ x: 1000, y: 450 }}
        locale={locale}
      />
    </Modal>
  );
};

export default MemberUniversityModal;